import { Routes, Route, useLocation } from 'react-router-dom'
import { Suspense, lazy, useState, useEffect } from 'react'
import ProtectedRoute from './components/shared/ProtectedRoute'
import HelmIcon from './components/shared/HelmIcon'

// Lazy load pages for better performance
const Landing = lazy(() => import('./pages/Landing'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Login = lazy(() => import('./pages/Login'))
const Onboarding = lazy(() => import('./pages/Onboarding'))

// Loading component with YachtState branding
function LoadingSpinner({ isDashboardLoading = false }) {
  return (
    <div className={`min-h-screen flex items-center justify-center ${isDashboardLoading ? 'bg-navy-900' : 'bg-gray-50'}`}>
      <div className="text-center">
        {isDashboardLoading ? (
          <>
            <div className="relative">
              <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto animate-pulse">
                <HelmIcon className="w-10 h-10 text-white" />
              </div>
              <div className="absolute inset-0 rounded-2xl border-2 border-gold-500 animate-ping opacity-30" />
            </div>
            <p className="mt-6 text-lg font-medium text-white">Loading YachtState</p>
            <div className="mt-4 flex items-center justify-center gap-1">
              <div className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy-600 mx-auto" />
            <p className="mt-4 text-sm text-gray-500">Loading...</p>
          </>
        )}
      </div>
    </div>
  )
}

// Animated page wrapper for smooth transitions
function AnimatedRoutes() {
  const location = useLocation()
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [displayLocation, setDisplayLocation] = useState(location)

  useEffect(() => {
    // Check if navigating to/from dashboard
    const isEnteringDashboard =
      location.pathname.startsWith('/dashboard') &&
      !displayLocation.pathname.startsWith('/dashboard')

    const isExitingDashboard =
      !location.pathname.startsWith('/dashboard') &&
      displayLocation.pathname.startsWith('/dashboard')

    if (isEnteringDashboard || isExitingDashboard) {
      setIsTransitioning(true)
      const timer = setTimeout(() => {
        setDisplayLocation(location)
        setIsTransitioning(false)
      }, 500)
      return () => clearTimeout(timer)
    } else {
      setDisplayLocation(location)
    }
  }, [location])

  // Determine if current route is dashboard
  const isDashboardRoute = displayLocation.pathname.startsWith('/dashboard')

  return (
    <div
      className={`transition-all duration-500 ease-out ${
        isTransitioning
          ? 'opacity-0 scale-[0.98]'
          : 'opacity-100 scale-100'
      }`}
    >
      <Suspense fallback={<LoadingSpinner isDashboardLoading={isDashboardRoute} />}>
        <Routes location={displayLocation}>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />

          {/* Onboarding - Protected */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute requiresYacht={true}>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Landing />} />
        </Routes>
      </Suspense>
    </div>
  )
}

export default function App() {
  return <AnimatedRoutes />
}
